import clsx from "clsx";
import { motion } from "framer-motion";

import { type TableHeaderRow } from "@devexpress/dx-react-grid-bootstrap4";
import { type ColumnDef } from "@features/landing-page-analytics/types";
import { useProductAnalyticsContext } from "@features/product-analytics/hooks";
import { LearnMoreTooltip } from "@features/sales-v3/top-level-filters/components/learn-more-tooltip";
import { useIsDarkMode } from "@hooks/use-is-dark-mode";
import { CreativeMetricType } from "@north-beam/nb-client-side";

import { NAME_COLUMN_ID, URL_COLUMN_ID } from "../constants";

const LEFT_ALIGNED: string[] = [URL_COLUMN_ID, NAME_COLUMN_ID];

const ABOUT_COLUMNS = [
  "spend",
  "cacPercentage",
  "roasPercentage",
  "proportional_spend",
  "proas",
  "pcac",
  "asp",
  "itemSoldRatio",
];

const columnDescription = (column: string) => {
  switch (column) {
    case "spend":
      return "The total spend across all campaigns that resulted in the sale of this product.";
    case "cacPercentage":
      return (
        "A score from 1 to 100 that represents how expensive it is to advertise " +
        "this product compared to all other products. A higher score is better, " +
        "indicating lower costs."
      );
    case "roasPercentage":
      return (
        "A score from 1 to 100 that measures how efficient advertising for this product is, " +
        "based on revenue generated per spend, compared to all other products. " +
        "A higher score indicates better advertising efficiency."
      );
    case "proportional_spend":
      return (
        "The proportion of total campaign spend attributed to the sale of this product " +
        "compared to the sale of other products. Product Spend = (Campaign Spend) × " +
        "Units of this product sold) ÷ (Total units of all products sold)"
      );
    case "proas":
      return "(Product Revenue) ÷ (Product Spend)";
    case "pcac":
      return "(Product Spend) ÷ (Orders)";
    case "asp":
      return "Average unit sale price";
    case "itemSoldRatio":
      return "Units per order";
    default:
      return "";
  }
};

export const TableHeaderContent = ({
  column,
  children,
}: TableHeaderRow.ContentProps) => {
  const columnDef = column as ColumnDef;
  const {
    tableState: { sorting },
    setSorting,
  } = useProductAnalyticsContext();

  const [isDarkMode] = useIsDarkMode();

  return (
    <>
      <div
        onClick={() => {
          setSorting({
            id: columnDef.name,
            order: sorting.order === "desc" ? "asc" : "desc",
          });
        }}
        className={clsx(
          "text-v2-primary font-bold uppercase text-xs w-full flex items-center px-2",
          {
            "justify-end": !LEFT_ALIGNED.includes(columnDef.name),
          },
        )}
      >
        <span
          className={clsx(
            "overflow-hidden min-w-[10px] flex items-center font-bold uppercase gap-1",
            {
              "flex-1": LEFT_ALIGNED.includes(columnDef.name),
            },
          )}
        >
          {columnDef.metric?.composition?.type ===
            CreativeMetricType.user_defined && (
            <i className="fa-solid fa-sharp fa-function text-sm text-cosmic mr-1" />
          )}
          {children}
          <div
            style={{
              color: "#808997",
              fontSize: 12,
              fontWeight: "400",
            }}
          >
            {ABOUT_COLUMNS.includes(column.name) && (
              <LearnMoreTooltip
                darkToolTip={isDarkMode}
                overlayClassName="bg-v2-primary rounded-lg p-4"
                title={columnDef.title}
                content={columnDescription(column.name)}
                link=""
                linkLabel=""
              />
            )}
          </div>
        </span>
        {sorting.id === columnDef.name && (
          <div
            className={clsx(
              "flex justify-center items-center bg-v2-highlight-information",
              "rounded w-6 h-6 ml-2 cursor-pointer text-v2-action",
            )}
            onClick={() => {
              setSorting({
                id: columnDef.name,
                order: sorting.order === "desc" ? "asc" : "desc",
              });
            }}
          >
            <motion.i
              initial={false}
              animate={{ rotate: sorting.order === "desc" ? 0 : 180 }}
              transition={{ duration: 0.2 }}
              className="fa-regular fa-sharp fa-arrow-down"
            />
          </div>
        )}
      </div>
      <div className="header-column-separator"></div>
    </>
  );
};
